<template>
  <div class="menu-container">
    <button
      type="button"
      class="kebab-btn"
      :class="{ active: open }"
      aria-label="Report options"
      @click.stop="open = !open"
    >
      &#8942;
    </button>

    <div v-if="open" class="dropdown-menu" :style="dropdownStyle" @click.stop>
      <button type="button" class="dropdown-item" @click="emit('view'); closeMenu()">
        <EyeIcon class="item-icon" />
        View
      </button>
      <button type="button" class="dropdown-item" @click="emit('toggle-favorite'); closeMenu()">
        <StarIconSolid v-if="isFavorite" class="item-icon item-icon--star" />
        <StarIconOutline v-else class="item-icon item-icon--star" />
        {{ isFavorite ? "Unfavorite" : "Favorite" }}
      </button>

      <!-- Standard report: one-click PDF download -->
      <template v-if="!report.isPaginated">
        <button
          type="button"
          class="dropdown-item"
          :disabled="isDownloading"
          :class="{ 'dropdown-item--disabled': isDownloading }"
          @click="triggerDownload('pdf')"
        >
          <ArrowDownTrayIcon class="item-icon" />
          {{ isDownloading ? "Downloading…" : "Download" }}
        </button>
      </template>

      <!-- Paginated report: expand to format submenu -->
      <template v-else>
        <button
          type="button"
          class="dropdown-item dropdown-item--submenu-toggle"
          @click.stop="showFormats = !showFormats"
        >
          <ArrowDownTrayIcon class="item-icon" />
          Download
          <span class="submenu-chevron" :class="{ open: showFormats }">&#9654;</span>
        </button>
        <div v-if="showFormats" class="submenu">
          <button
            type="button"
            class="dropdown-item dropdown-item--sub"
            :disabled="isDownloading"
            :class="{ 'dropdown-item--disabled': isDownloading }"
            @click="triggerDownload('pdf')"
          >
            {{ isDownloading && downloadingFormat === 'pdf' ? "Downloading…" : "PDF" }}
          </button>
          <button
            type="button"
            class="dropdown-item dropdown-item--sub"
            :disabled="isDownloading"
            :class="{ 'dropdown-item--disabled': isDownloading }"
            @click="triggerDownload('xlsx')"
          >
            {{ isDownloading && downloadingFormat === 'xlsx' ? "Downloading…" : "Excel" }}
          </button>
          <button
            type="button"
            class="dropdown-item dropdown-item--sub"
            :disabled="isDownloading"
            :class="{ 'dropdown-item--disabled': isDownloading }"
            @click="triggerDownload('csv')"
          >
            {{ isDownloading && downloadingFormat === 'csv' ? "Downloading…" : "CSV" }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { EyeIcon, ArrowDownTrayIcon, StarIcon as StarIconSolid } from "@heroicons/vue/24/solid";
import { StarIcon as StarIconOutline } from "@heroicons/vue/24/outline";
import { useReportsStore } from "@/store/store/reportsStore";
import type { ReportItem, ReportExportFormat } from "@/types/reports";

const MENU_WIDTH = 160;
const MENU_HEIGHT = 180;

const props = defineProps<{ report: ReportItem }>();

const reportsStore = useReportsStore();
const isFavorite = computed(() => reportsStore.favoriteIds.has(props.report.id));
const isDownloading = computed(() => reportsStore.downloadingId === props.report.id);

const emit = defineEmits<{
  (e: "view"): void;
  (e: "toggle-favorite"): void;
  (e: "download", format: ReportExportFormat): void;
}>();

const open = ref(false);
const showFormats = ref(false);
const downloadingFormat = ref<ReportExportFormat | null>(null);
const contextX = ref<number | null>(null);
const contextY = ref<number | null>(null);

const dropdownStyle = computed(() => {
  if (contextX.value === null) return {};
  const x = Math.min(contextX.value, window.innerWidth - MENU_WIDTH - 8);
  const y = Math.min(contextY.value!, window.innerHeight - MENU_HEIGHT - 8);
  return {
    position: "fixed" as const,
    left: `${x}px`,
    top: `${y}px`,
    right: "auto",
  };
});

function closeMenu() {
  open.value = false;
  showFormats.value = false;
  contextX.value = null;
  contextY.value = null;
}

function openAt(x: number, y: number) {
  contextX.value = x;
  contextY.value = y;
  open.value = true;
}

function triggerDownload(format: ReportExportFormat) {
  downloadingFormat.value = format;
  emit("download", format);
  closeMenu();
}

defineExpose({ openAt });

onMounted(() => document.addEventListener("click", closeMenu));
onUnmounted(() => document.removeEventListener("click", closeMenu));
</script>

<style scoped>
.menu-container {
  position: relative;
  flex-shrink: 0;
  width: 32px;
}

.kebab-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 4px 8px;
  color: #999;
  border-radius: 4px;
  line-height: 1;
  transition: all 0.2s;
}

.kebab-btn:hover,
.kebab-btn.active {
  background: #f0f0f0;
  color: #333;
}

.dropdown-menu {
  position: absolute;
  right: 0;
  top: 100%;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 150px;
  z-index: 100;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  text-align: left;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item--disabled {
  color: #bbb;
  cursor: not-allowed;
}

.dropdown-item--disabled:hover {
  background: none;
}

.dropdown-item--submenu-toggle {
  justify-content: flex-start;
}

.submenu-chevron {
  margin-left: auto;
  font-size: 10px;
  color: #999;
  transition: transform 0.15s;
  display: inline-block;
}

.submenu-chevron.open {
  transform: rotate(90deg);
}

.submenu {
  background: #fafafa;
  border-top: 1px solid #eee;
}

.dropdown-item--sub {
  padding-left: 38px;
  font-size: 13px;
}

.item-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #555;
}

.item-icon--star {
  color: #f0b429;
}
</style>
